import type { ReactNode } from 'react'

import { Spinner } from '@aeris/ui'

/**
 * O que a tela mostra enquanto uma importação está sendo gravada.
 *
 * Existe por causa de um relato concreto: *"já cliquei no botão importar mil
 * vezes, não dá pra saber se está sendo enviado ou não"*.
 *
 * O botão já se desabilitava e já mostrava um spinner de dezesseis pixels. Isso
 * não bastou, e a razão é aritmética: gravar três mil linhas leva **cerca de
 * dez segundos**, e durante dez segundos uma tela que muda um ícone pequeno e
 * nada mais é indistinguível de uma tela travada. Quem está migrando o próprio
 * negócio clica de novo — e não deveria precisar adivinhar.
 *
 * Então o estado de "gravando" ocupa o lugar do botão, diz **quantas linhas**
 * estão sendo escritas, e diz para não fechar a página. Não há barra de
 * progresso porque não haveria como enchê-la honestamente: a escrita é uma
 * transação só, e uma barra que anda sozinha inventa um progresso que ninguém
 * mediu.
 */
export const ImportProgress = ({
  rows,
  noun,
}: {
  rows: number
  /** "produtos" ou "clientes" — o que está sendo gravado. */
  noun: string
}): ReactNode => (
  <section
    role="status"
    aria-live="polite"
    className="flex items-center gap-4 rounded-(--radius-md) border border-(--brand-primary) bg-(--surface-raised) p-6"
  >
    <Spinner className="h-6 w-6 shrink-0" />
    <div>
      <p className="text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
        Gravando {rows.toLocaleString('pt-BR')} {noun}…
      </p>
      <p className="text-(length:--text-sm) text-(--text-secondary)">
        Isto leva alguns segundos e é uma operação só: ou entra tudo, ou não
        entra nada. Não feche a página — clicar de novo não acelera.
      </p>
    </div>
  </section>
)
